import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Dataflow01, Expand03, Trash02 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import z from "zod";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { Drawer } from "@/components/Molecules/Drawer/Drawer";
import { TabContentWrapper } from "@/components/Molecules/TabContentWrapper";
import { DeleteLifecycleModal } from "@/components/Organisms/DeleteLifecycleModal/DeleteLifecycleModal";
import { statusLifecycleDisplayLabel } from "@/forms/ticket-type/create-ticket-type/ticket-type-lifecycle.utils";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useUpdateLifecycleDetails,
	useUpdateStatusLifecycle,
} from "@/services/lifecycle/lifecycle.mutations";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import { isLifecycleActive } from "@/utils/lifecycle-admin";
import {
	canShowActivityTab,
	getLifecycleDisplayName,
} from "@/utils/lifecycle-detail/lifecycle-detail.utils";
import {
	LifecycleDrawerContext,
	type LifecycleDrawerContextValue,
} from "@/utils/lifecycle-detail/lifecycle-drawer-context";
import {
	clearPendingLifecycleDraft,
	getPendingLifecycleDraft,
	setPendingLifecycleDraft,
} from "@/utils/lifecycle-detail/lifecycle-pending-drafts";
import { persistLifecycleNameAndDescription } from "@/utils/lifecycle-detail/persist-lifecycle-name-description";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
});

type LifecycleTab =
	| "details"
	| "statuses"
	| "transitions"
	| "rules"
	| "activity";

function getCurrentTab(pathname: string): LifecycleTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "statuses" ||
		segment === "transitions" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId",
)({
	component: OrgLifecycleDrawerRoute,
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/lifecycles/${params.lifecycleId}`,
		);
		if (exactRoot) {
			throw redirect({
				to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
				params: { orgId: params.orgId, lifecycleId: params.lifecycleId },
				search: { mode: "view" },
			});
		}
	},
});

function OrgLifecycleDrawerRoute() {
	const { t } = useAppTranslation();
	const viewAllOrgsQuery = useQuery(
		authQueryOptions.checkAction({
			name: "ViewAllOrganizations",
			entityType: null,
			entityId: null,
		}),
	);
	const isPlatformAdmin = viewAllOrgsQuery.data ?? false;
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId",
	});

	const isFullRoute = /\/configuration\/lifecycles\/[^/]+\/full/.test(pathname);

	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const updateLifecycleMutation = useUpdateStatusLifecycle();
	const updateLifecycleDetailsMutation = useUpdateLifecycleDetails();
	const detailQuery = useQuery(
		lifecycleQueryOptions.statusLifecycleDetail(
			params.lifecycleId,
			params.orgId,
		),
	);

	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteStatusLifecycle",
			entityType: "StatusLifecycle",
			entityId: params.lifecycleId,
		}),
	});

	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditStatusLifecycle",
			entityType: "StatusLifecycle",
			entityId: params.lifecycleId,
		}),
	});

	const canEditDetailsQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditStatusLifecycleDetails",
			entityType: "StatusLifecycle",
			entityId: params.lifecycleId,
		}),
	});

	const lifecycle = detailQuery.data ?? null;

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const latestDraftsRef = useRef({
		draftName: "",
		draftDescription: "",
		editedName: "",
		editedDescription: "",
	});
	latestDraftsRef.current = {
		draftName,
		draftDescription,
		editedName,
		editedDescription,
	};

	const syncPendingDraft = (
		lifecycleId: string,
		next: { draftName?: string; draftDescription?: string },
	) => {
		const drafts = {
			draftName: next.draftName ?? latestDraftsRef.current.draftName,
			draftDescription:
				next.draftDescription ?? latestDraftsRef.current.draftDescription,
			editedName: latestDraftsRef.current.editedName,
			editedDescription: latestDraftsRef.current.editedDescription,
		};
		const isDirty =
			drafts.draftName.trim() !== drafts.editedName.trim() ||
			drafts.draftDescription !== drafts.editedDescription;
		if (isDirty) {
			setPendingLifecycleDraft(lifecycleId, {
				draftName: drafts.draftName,
				draftDescription: drafts.draftDescription,
			});
			return;
		}
		clearPendingLifecycleDraft(lifecycleId);
	};

	const handleDraftNameChange = (name: string) => {
		setDraftName(name);
		if (lifecycle?.id) syncPendingDraft(lifecycle.id, { draftName: name });
	};

	const handleDraftDescriptionChange = (description: string) => {
		setDraftDescription(description);
		if (lifecycle?.id) {
			syncPendingDraft(lifecycle.id, { draftDescription: description });
		}
	};

	useEffect(() => {
		if (!isFullRoute) setIsOpen(true);
	}, [isFullRoute]);

	useEffect(() => {
		if (!lifecycle?.id) return;

		const pending = getPendingLifecycleDraft(lifecycle.id);
		if (pending) {
			const name = getLifecycleDisplayName(lifecycle);
			const description = lifecycle.description ?? "";
			setEditedName(name);
			setEditedDescription(description);
			setDraftName(pending.draftName);
			setDraftDescription(pending.draftDescription);
			return;
		}

		const name = getLifecycleDisplayName(lifecycle);
		const description = lifecycle.description ?? "";
		setEditedName(name);
		setEditedDescription(description);
		setDraftName(name);
		setDraftDescription(description);
	}, [lifecycle]);

	const handleClose = () => {
		if (lifecycle?.id) clearPendingLifecycleDraft(lifecycle.id);
		setIsOpen(false);
		setTimeout(() => {
			navigate({
				to: `/org-admin/${params.orgId}/configuration/lifecycles`,
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		if (!open && search.mode === "edit" && lifecycle !== null) return;
		setIsOpen(open);
		if (!open) handleClose();
	};

	const loadedLifecycle = lifecycle ?? undefined;

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || lifecycle === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && lifecycle !== null;

	const displayLifecycle =
		loadedLifecycle && isDetailReady
			? {
					...loadedLifecycle,
					name: editedName,
					description: editedDescription,
				}
			: null;

	const headerTitle =
		displayLifecycle !== null
			? getLifecycleDisplayName(displayLifecycle)
			: t("admin.lifecycles.drawer.header.titlePlaceholder");

	const isEditing = search.mode === "edit";
	const showCloseButton = !(isEditing && lifecycle !== null);
	const currentTab = getCurrentTab(pathname);

	const tabs: { id: LifecycleTab; label: string }[] = useMemo(() => {
		if (!loadedLifecycle || !isDetailReady) return [];
		const showActivity = canShowActivityTab("organization", loadedLifecycle);
		return [
			{ id: "details", label: t("admin.lifecycles.drawer.tabs.details") },
			{ id: "statuses", label: t("admin.lifecycles.drawer.tabs.statuses") },
			{
				id: "transitions",
				label: t("admin.lifecycles.drawer.tabs.transitions"),
			},
			...(showActivity
				? [
						{
							id: "activity" as const,
							label: t("admin.lifecycles.drawer.tabs.activity"),
						},
					]
				: []),
		];
	}, [loadedLifecycle, isDetailReady, t]);

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [
				{
					id: currentTab,
					label: `Edit ${currentTabInfo.label}`,
				},
			]
		: [];

	const canDelete =
		!!loadedLifecycle && isDetailReady && !!canDeleteQuery.data && !isEditing;

	const contextValue: LifecycleDrawerContextValue | null =
		loadedLifecycle && isDetailReady && displayLifecycle
			? {
					scope: "organization",
					lifecycle: displayLifecycle,
					mode: search.mode,
					setMode: (mode) => {
						if (isFullRoute) {
							navigate({
								to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
								params: {
									orgId: params.orgId,
									lifecycleId: params.lifecycleId,
								},
								search: { tab: "details", mode },
							});
						} else {
							navigate({
								to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
								params: {
									orgId: params.orgId,
									lifecycleId: params.lifecycleId,
								},
								search: { mode },
							});
						}
					},
					canEditDetails: !!canEditDetailsQuery.data,
					canEditStructure: !!canEditQuery.data,
					isDraft: !isLifecycleActive(displayLifecycle),
					canEdit: !!canEditDetailsQuery.data || !!canEditQuery.data,
					draftName,
					draftDescription,
					setDraftName: handleDraftNameChange,
					setDraftDescription: handleDraftDescriptionChange,
					organizationId: params.orgId,
					isSaving:
						updateLifecycleMutation.isPending ||
						updateLifecycleDetailsMutation.isPending,
					isValid: draftName.trim().length > 0,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription,
					saveDraft: async () => {
						try {
							const nextName = draftName.trim();
							if (nextName.length === 0) return;
							const nextDescription = draftDescription;
							const hasChanges =
								nextName !== editedName ||
								nextDescription !== editedDescription;

							if (!hasChanges) {
								if (isFullRoute) {
									navigate({
										to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
										params: {
											orgId: params.orgId,
											lifecycleId: params.lifecycleId,
										},
										search: { tab: "details", mode: "view" },
									});
								} else {
									navigate({
										to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
										params: {
											orgId: params.orgId,
											lifecycleId: params.lifecycleId,
										},
										search: { mode: "view" },
									});
								}
								return;
							}

							const persisted = await persistLifecycleNameAndDescription({
								canEditStructure: !!canEditQuery.data,
								canEditDetails: !!canEditDetailsQuery.data,
								lifecycleId: params.lifecycleId,
								organizationId: params.orgId,
								name: nextName,
								description:
									nextDescription.trim().length > 0 ? nextDescription : null,
								lifecycle: loadedLifecycle,
								updateStructure: (vars) =>
									updateLifecycleMutation.mutateAsync(vars),
								updateDetails: (vars) =>
									updateLifecycleDetailsMutation.mutateAsync(vars),
							});
							if (!persisted) return;
							showSuccessToast({
								message: t("admin.lifecycles.drawer.details.toast.saveSuccess"),
							});
							setEditedName(nextName);
							setEditedDescription(nextDescription);
							if (isFullRoute) {
								navigate({
									to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
									params: {
										orgId: params.orgId,
										lifecycleId: params.lifecycleId,
									},
									search: { tab: "details", mode: "view" },
								});
							} else {
								navigate({
									to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
									params: {
										orgId: params.orgId,
										lifecycleId: params.lifecycleId,
									},
									search: { mode: "view" },
								});
							}
						} catch (error) {
							showApiErrorToast({
								message: t("admin.shared.formEditor.saveError"),
								error,
							});
							console.error("Failed to save lifecycle edits:", error);
						}
					},
					cancelDraft: () => {
						handleDraftNameChange(editedName);
						handleDraftDescriptionChange(editedDescription);
						if (isFullRoute) {
							navigate({
								to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/full",
								params: {
									orgId: params.orgId,
									lifecycleId: params.lifecycleId,
								},
								search: { tab: "details", mode: "view" },
							});
						} else {
							navigate({
								to: "/org-admin/$orgId/configuration/lifecycles/$lifecycleId/details",
								params: {
									orgId: params.orgId,
									lifecycleId: params.lifecycleId,
								},
								search: { mode: "view" },
							});
						}
					},
				}
			: null;

	if (isFullRoute) {
		return contextValue ? (
			<LifecycleDrawerContext.Provider value={contextValue}>
				<Outlet />
			</LifecycleDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	// useRouterState updates before the component unmounts, so during navigation
	// away from any $lifecycleId route the pathname briefly shows the new URL.
	// Guard against mounting the Drawer during that window.
	const isOnLifecycleSubroute = /\/configuration\/lifecycles\/[^/]+\//.test(
		pathname,
	);

	if (!isOnLifecycleSubroute) {
		return null;
	}

	return (
		<>
			<Drawer
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				width="40%"
				backdropPreset="transparent"
			>
				{({ close }) => (
					<>
						<Drawer.Header
							variant="document"
							showDivider={false}
							icon={
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-50 ring-1 ring-slate-200">
									<Dataflow01 className="size-5 text-slate-600" aria-hidden />
								</div>
							}
							iconSize="md"
							iconTheme="light"
							title={headerTitle}
							description={t("admin.lifecycles.drawer.header.subtitle")}
							showCloseButton={showCloseButton}
							closeButtonClassName="absolute top-2 right-2 z-10"
							onClose={() => {
								close();
								handleClose();
							}}
						/>
						<Drawer.Content>
							{isDetailLoading ? (
								<div className="flex h-full items-center justify-center">
									<LoadingIndicator
										type="line-simple"
										size="xl"
										supportingText
									/>
								</div>
							) : null}
							{isDetailError ? (
								<div className="flex h-full flex-col items-center justify-center gap-3 text-center">
									<p className="text-sm font-semibold text-error">
										{t("admin.lifecycles.drawer.errors.loadFailed")}
									</p>
									<p className="text-sm text-gray-500">
										{t("admin.lifecycles.drawer.errors.notFound")}
									</p>
								</div>
							) : null}
							{isDetailReady && contextValue ? (
								<LifecycleDrawerContext.Provider value={contextValue}>
									<div className="space-y-4">
										<NativeSelect
											aria-label={t("admin.lifecycles.drawer.header.subtitle")}
											value={currentTab}
											onChange={(event) =>
												navigate({
													to: `/org-admin/${params.orgId}/configuration/lifecycles/${params.lifecycleId}/${event.target.value}`,
													search: { mode: search.mode },
												})
											}
											options={(isEditing ? editModeTabs : tabs).map((tab) => ({
												label: tab.label,
												value: tab.id,
											}))}
											className="w-80 md:hidden"
										/>
										<Tabs
											selectedKey={currentTab}
											onSelectionChange={(event) =>
												navigate({
													to: `/org-admin/${params.orgId}/configuration/lifecycles/${params.lifecycleId}/${event}`,
													search: { mode: search.mode },
												})
											}
											className="w-full max-md:hidden"
										>
											<Tabs.List
												type="underline"
												items={isEditing ? editModeTabs : tabs}
												fullWidth
											>
												{(tab) => <Tabs.Item {...tab} />}
											</Tabs.List>
										</Tabs>
										<TabContentWrapper tabKey={currentTab}>
											<Outlet />
										</TabContentWrapper>
									</div>
								</LifecycleDrawerContext.Provider>
							) : null}
						</Drawer.Content>
						<Drawer.Footer>
							{isDetailLoading || isDetailError ? (
								<div className="flex w-full justify-end">
									<Button
										data-testid="lifecycle-drawer-close-btn"
										color="secondary"
										size="md"
										onClick={handleClose}
										className="hover:bg-(--color-hover) hover:text-current"
									>
										{t("admin.lifecycles.drawer.footer.close")}
									</Button>
								</div>
							) : contextValue ? (
								<div className="flex w-full items-center justify-between">
									<div>
										{canDelete ? (
											<Button
												data-testid="lifecycle-drawer-delete-btn"
												color="secondary-destructive"
												size="md"
												iconLeading={Trash02}
												onClick={() => setDeleteModalOpen(true)}
											>
												{t("admin.lifecycles.drawer.footer.deleteLifecycle")}
											</Button>
										) : (
											<div />
										)}
									</div>
									<div className="flex items-center gap-3">
										{isEditing ? (
											<>
												<Button
													data-testid="lifecycle-drawer-cancel-btn"
													color="secondary"
													size="md"
													onClick={contextValue.cancelDraft}
												>
													{t("admin.lifecycles.drawer.actions.cancel")}
												</Button>
												<Button
													data-testid="lifecycle-drawer-save-btn"
													color="primary"
													size="md"
													isLoading={contextValue.isSaving}
													isDisabled={
														!contextValue.isValid || contextValue.isSaving
													}
													onClick={contextValue.saveDraft}
												>
													{t("admin.lifecycles.drawer.actions.save")}
												</Button>
											</>
										) : (
											<Button
												data-testid="lifecycle-drawer-close-btn"
												color="secondary"
												size="md"
												onClick={handleClose}
												className="hover:bg-(--color-hover) hover:text-current"
											>
												{t("admin.lifecycles.drawer.footer.close")}
											</Button>
										)}
									</div>
								</div>
							) : null}
						</Drawer.Footer>
						{isDetailReady ? (
							<Button
								data-testid="lifecycle-drawer-fullview-btn"
								size="sm"
								color="secondary"
								iconLeading={Expand03}
								onClick={() => {
									navigate({
										to: `/org-admin/${params.orgId}/configuration/lifecycles/${params.lifecycleId}/full`,
									});
								}}
								className="absolute top-3 right-14 z-10"
							>
								{t("admin.lifecycles.drawer.header.fullView")}
							</Button>
						) : null}
					</>
				)}
			</Drawer>

			<DeleteLifecycleModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				lifecycleId={params.lifecycleId}
				lifecycleName={lifecycle ? statusLifecycleDisplayLabel(lifecycle) : ""}
				organizationId={isPlatformAdmin ? params.orgId : undefined}
				scope="organization"
			/>
		</>
	);
}
